import React from 'react';
import { CustomerContact } from '../../../types/customer-contact';
import { Customer } from '../../../types';
import { emptyContactForm } from '../utils/contactHelpers';

type FormState = typeof emptyContactForm;

interface ContactEditFormProps {
  form: FormState;
  customers: Customer[];
  submitting: boolean;
  panelError: string | null;
  selectedContact: CustomerContact | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

const inputClass =
  'w-full theme-border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary';

const ContactEditForm: React.FC<ContactEditFormProps> = ({
  form,
  customers,
  submitting,
  panelError,
  selectedContact,
  onChange,
  onSubmit,
  onCancel,
}) => (
  <form onSubmit={onSubmit} className="space-y-3">
    {panelError && (
      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
        {panelError}
      </div>
    )}

    <div className="rounded-xl theme-border theme-card p-3">
      <label className="block text-sm font-medium theme-text-primary mb-0.5">
        Customer <span className="text-red-500">*</span>
      </label>
      <select name="customer_id" value={form.customer_id} onChange={onChange} required className={inputClass}>
        <option value="">Select customer</option>
        {customers.map((c) => (
          <option key={c.id} value={c.id}>
            {c.item}
          </option>
        ))}
      </select>
    </div>

    <div className="rounded-xl theme-border theme-card p-3">
      <label className="block text-sm font-medium theme-text-primary mb-0.5">
        Name <span className="text-red-500">*</span>
      </label>
      <input
        name="name"
        value={form.name}
        onChange={onChange}
        required
        className={inputClass}
        placeholder="Enter contact name"
      />
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-xl theme-border theme-card p-3">
        <label className="block text-sm font-medium theme-text-primary mb-0.5">Email</label>
        <input name="email" type="email" value={form.email} onChange={onChange} className={inputClass} placeholder="Enter email" />
      </div>
      <div className="rounded-xl theme-border theme-card p-3">
        <label className="block text-sm font-medium theme-text-primary mb-0.5">Phone</label>
        <input name="phone" value={form.phone} onChange={onChange} className={inputClass} placeholder="Enter phone" />
      </div>
    </div>

    <div className="rounded-xl theme-border theme-card p-3">
      <label className="block text-sm font-medium theme-text-primary mb-0.5">Title</label>
      <select name="title" value={form.title} onChange={onChange} className={inputClass}>
        <option value="">Select title</option>
        <option value="Mr.">Mr.</option>
        <option value="Miss">Miss</option>
        <option value="Ms.">Ms.</option>
      </select>
    </div>

    <div className="rounded-xl theme-border theme-card p-3">
      <label className="block text-sm font-medium theme-text-primary mb-0.5">Street</label>
      <input name="street" value={form.street} onChange={onChange} className={inputClass} placeholder="Enter street address" />
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-xl theme-border theme-card p-3">
        <label className="block text-sm font-medium theme-text-primary mb-0.5">City</label>
        <input name="city" value={form.city} onChange={onChange} className={inputClass} placeholder="Enter city" />
      </div>
      <div className="rounded-xl theme-border theme-card p-3">
        <label className="block text-sm font-medium theme-text-primary mb-0.5">State</label>
        <input name="state" value={form.state} onChange={onChange} className={inputClass} placeholder="Enter state" />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-xl theme-border theme-card p-3">
        <label className="block text-sm font-medium theme-text-primary mb-0.5">ZIP</label>
        <input name="zip" value={form.zip} onChange={onChange} className={inputClass} placeholder="Enter ZIP code" />
      </div>
      <div className="rounded-xl theme-border theme-card p-3">
        <label className="block text-sm font-medium theme-text-primary mb-0.5">Country</label>
        <input name="country" value={form.country} onChange={onChange} className={inputClass} placeholder="Enter country" />
      </div>
    </div>

    <div className="rounded-xl theme-border theme-card p-3">
      <label className="block text-sm font-medium theme-text-primary mb-0.5">Notes</label>
      <textarea
        name="notes"
        value={form.notes}
        onChange={onChange}
        rows={3}
        className={inputClass}
        placeholder="Enter notes (optional)"
      />
    </div>

    <div className="rounded-xl theme-border theme-card p-3">
      <div className="flex items-center gap-1.5">
        <input
          type="checkbox"
          name="is_primary"
          checked={form.is_primary}
          onChange={onChange}
          className="w-3.5 h-3.5 text-blue-600 theme-border rounded focus:ring-blue-500"
        />
        <label className="text-sm font-medium theme-text-primary">Primary Contact</label>
      </div>
    </div>

    <div className="flex gap-2 pt-3">
      <button
        type="button"
        onClick={onCancel}
        className="flex-1 px-3 py-1.5 theme-border rounded theme-bg-secondary theme-text-primary hover:opacity-80 text-sm"
        disabled={submitting}
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={submitting}
        className="flex-1 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded hover:shadow-lg transition-shadow disabled:opacity-50 text-sm"
      >
        {submitting ? 'Saving...' : selectedContact ? 'Save Changes' : 'Create Contact'}
      </button>
    </div>
  </form>
);

export default ContactEditForm;
